import { UploadCloud, X } from "lucide-react";
import { useId, useRef, useState, type DragEvent } from "react";
import { useTranslation } from "react-i18next";
import { checkUpload, UPLOAD_ACCEPT } from "./upload";
import { cn } from "@/lib/utils";

export type PickedFile = { file: File; error: string | null };

/** Drag files here or choose them. Each file is checked against the upload rules straight away. */
export function FileDropZone({ files, onChange }: { files: PickedFile[]; onChange: (files: PickedFile[]) => void }) {
  const { t } = useTranslation();
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const add = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const picked = Array.from(list).map((file) => ({ file, error: checkUpload(file) }));
    onChange([...files, ...picked]);
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setOver(false);
    add(e.dataTransfer.files);
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={cn(
          "flex flex-col items-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors duration-fast",
          over ? "border-accent bg-secondary-subtle" : "border-border-strong bg-surface",
        )}
      >
        <UploadCloud aria-hidden="true" className="h-8 w-8 text-accent" />
        <p className="font-semibold text-heading">{t("files.dropTitle")}</p>
        <p className="text-sm text-fg-muted" id={`${id}-hint`}>
          {t("files.fileHint")}
        </p>
        <label
          htmlFor={id}
          className="inline-flex min-h-11 cursor-pointer items-center rounded-pill border-2 border-secondary bg-surface px-6 font-heading font-semibold text-heading transition-colors duration-fast hover:bg-secondary-subtle has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-focus"
        >
          {t("files.chooseFiles")}
          <input
            ref={input}
            id={id}
            type="file"
            multiple
            accept={UPLOAD_ACCEPT}
            aria-describedby={`${id}-hint`}
            className="sr-only"
            onChange={(e) => {
              add(e.target.files);
              if (input.current) input.current.value = "";
            }}
          />
        </label>
      </div>
      {files.length > 0 ? (
        <ul className="flex flex-col gap-2" aria-label={t("files.chosen")}>
          {files.map((f, i) => (
            <li
              key={`${f.file.name}-${i}`}
              className={cn(
                "flex items-center gap-3 rounded-md border px-4 py-2",
                f.error ? "border-danger-muted bg-danger-subtle" : "border-border bg-surface",
              )}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{f.file.name}</span>
                {f.error ? <span className="block text-sm text-danger-fg">{f.error}</span> : null}
              </span>
              <button
                type="button"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                aria-label={t("files.removeChosen", { name: f.file.name })}
                className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full text-fg-muted hover:bg-surface-muted"
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
